import { collection, addDoc, getDocs, query, where, Timestamp, deleteDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { habitaciones } from './habitaciones';

// Constante para definir la zona horaria de Santiago de Chile
const ZONA_HORARIA_CHILE = 'America/Santiago';

// Función para normalizar el formato de fecha, asegurándose que sea yyyy-mm-dd
const normalizarFormatoFecha = (fecha) => {
  // Si ya está en formato yyyy-mm-dd, retornar sin cambios
  if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
    return fecha;
  }
  
  // Si está en formato dd-mm-yyyy, convertir a yyyy-mm-dd
  if (/^\d{2}-\d{2}-\d{4}$/.test(fecha)) {
    const [dia, mes, anio] = fecha.split('-');
    return `${anio}-${mes}-${dia}`;
  }
  
  // Intenta parsear como fecha y reformatear
  try {
    const fechaObj = new Date(fecha);
    if (isNaN(fechaObj.getTime())) {
      throw new Error(`Formato de fecha inválido: ${fecha}`);
    }
    
    // Formatear la fecha usando la zona horaria de Chile
    const opciones = { timeZone: ZONA_HORARIA_CHILE };
    const anio = fechaObj.toLocaleString('es-CL', {...opciones, year: 'numeric'});
    const mes = fechaObj.toLocaleString('es-CL', {...opciones, month: '2-digit'});
    const dia = fechaObj.toLocaleString('es-CL', {...opciones, day: '2-digit'});
    
    return `${anio}-${mes}-${dia}`;
  } catch (error) {
    console.error(`Error al normalizar fecha: ${fecha}`, error);
    return fecha; // Retornar sin cambios en caso de error
  }
};

// Función para crear un objeto Date con la hora local de Chile
const crearFechaChile = (anio, mes, dia) => {
  // Crear una fecha en formato ISO con la zona horaria de Chile
  const fechaStr = `${anio}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}T12:00:00`;
  
  // Opciones para especificar la zona horaria
  const opciones = { 
    timeZone: ZONA_HORARIA_CHILE,
  };
  
  // Crear una fecha que represente las 12 del mediodía en Chile
  return new Date(fechaStr);
};

// Función para obtener todas las reservas
export const obtenerReservas = async () => {
  try {
    const reservasRef = collection(db, 'reservas');
    const querySnapshot = await getDocs(reservasRef);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      
      // Verificar si las fechas son Timestamps de Firebase o strings
      const fechaEntrada = data.fechaEntrada instanceof Timestamp 
        ? data.fechaEntrada.toDate().toLocaleString('es-CL', { 
            timeZone: ZONA_HORARIA_CHILE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).split('/').join('-')
        : data.fechaEntrada;
        
      const fechaSalida = data.fechaSalida instanceof Timestamp 
        ? data.fechaSalida.toDate().toLocaleString('es-CL', { 
            timeZone: ZONA_HORARIA_CHILE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).split('/').join('-')
        : data.fechaSalida;
        
      return {
        id: doc.id,
        ...data,
        fechaEntrada,
        fechaSalida
      };
    });
  } catch (error) {
    console.error('Error al obtener las reservas:', error);
    throw error;
  }
};

// Función para guardar una nueva reserva
export const guardarReserva = async (reserva) => {
  try {
    console.log('Fecha entrada original:', reserva.fechaEntrada);
    console.log('Fecha salida original:', reserva.fechaSalida);
    
    // Normalizar las fechas a formato yyyy-mm-dd
    const fechaEntradaNormalizada = normalizarFormatoFecha(reserva.fechaEntrada);
    const fechaSalidaNormalizada = normalizarFormatoFecha(reserva.fechaSalida);
    
    console.log('Fecha entrada normalizada:', fechaEntradaNormalizada);
    console.log('Fecha salida normalizada:', fechaSalidaNormalizada);

    // Parseamos manualmente la fecha para evitar problemas de interpretación
    const [anioEntrada, mesEntrada, diaEntrada] = fechaEntradaNormalizada.split('-');
    const [anioSalida, mesSalida, diaSalida] = fechaSalidaNormalizada.split('-');
    
    // Creamos fechas que respeten la zona horaria de Chile
    const fechaEntradaObj = crearFechaChile(anioEntrada, mesEntrada, diaEntrada);
    const fechaSalidaObj = crearFechaChile(anioSalida, mesSalida, diaSalida);
    
    console.log('Fecha entrada objeto con zona Chile:', fechaEntradaObj.toString());
    console.log('Fecha salida objeto con zona Chile:', fechaSalidaObj.toString());

    // Crear timestamps para Firebase usando estos objetos Date
    const fechaEntradaTimestamp = Timestamp.fromDate(fechaEntradaObj);
    const fechaSalidaTimestamp = Timestamp.fromDate(fechaSalidaObj);

    // Usar el código de reserva proporcionado o generar uno nuevo
    const codigoReserva = reserva.codigoReserva || Math.floor(100000 + Math.random() * 900000).toString();

    const reservaData = {
      ...reserva,
      fechaEntrada: fechaEntradaTimestamp,
      fechaSalida: fechaSalidaTimestamp,
      codigoReserva: codigoReserva,
      fechaCreacion: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'reservas'), reservaData);
    return { id: docRef.id, codigoReserva };
  } catch (error) {
    console.error('Error al guardar la reserva:', error);
    throw error;
  }
};

// Función para verificar disponibilidad de habitación
export const verificarDisponibilidadHabitacion = async (habitacionId, fechaEntrada, fechaSalida) => {
  try {
    console.log(`🔍 Verificando disponibilidad para habitación ID: ${habitacionId} del ${fechaEntrada} al ${fechaSalida}`);
    
    // Normalizar las fechas
    const fechaEntradaNormalizada = normalizarFormatoFecha(fechaEntrada);
    const fechaSalidaNormalizada = normalizarFormatoFecha(fechaSalida);
    
    // Parseamos manualmente la fecha
    const [anioEntrada, mesEntrada, diaEntrada] = fechaEntradaNormalizada.split('-');
    const [anioSalida, mesSalida, diaSalida] = fechaSalidaNormalizada.split('-');
    
    // Crear fechas con la zona horaria de Chile
    const fechaEntradaObj = crearFechaChile(anioEntrada, mesEntrada, diaEntrada);
    const fechaSalidaObj = crearFechaChile(anioSalida, mesSalida, diaSalida);
    
    // Convertir a string para comparación consistente
    const habitacionIdStr = habitacionId.toString();

    // IMPORTANTE: Asegurarnos de buscar tanto por string como por número
    // Firestore no hace conversión automática de tipos
    const habitacionIdNum = parseInt(habitacionId);
    
    // Crear dos consultas: una para habitacionId como string y otra como número
    const q1 = query(
      collection(db, 'reservas'),
      where('habitacionId', '==', habitacionIdStr)
    );
    
    const q2 = query(
      collection(db, 'reservas'),
      where('habitacionId', '==', habitacionIdNum)
    );
    
    // También buscar en el campo roomId que se usa en algunas partes del código
    const q3 = query(
      collection(db, 'reservas'),
      where('roomId', '==', habitacionIdStr)
    );
    
    const q4 = query(
      collection(db, 'reservas'),
      where('roomId', '==', habitacionIdNum)
    );

    // Ejecutar todas las consultas
    const [snapshot1, snapshot2, snapshot3, snapshot4] = await Promise.all([
      getDocs(q1),
      getDocs(q2),
      getDocs(q3),
      getDocs(q4)
    ]);
    
    // Combinar los resultados de todas las consultas, eliminando duplicados por ID
    const reservasMap = new Map();
    
    const processSnapshot = (snapshot, source) => {
      snapshot.docs.forEach(doc => {
        if (!reservasMap.has(doc.id)) {
          console.log(`📄 Reserva encontrada en ${source}: ${doc.id}`);
          reservasMap.set(doc.id, { id: doc.id, ...doc.data() });
        }
      });
    };
    
    processSnapshot(snapshot1, 'habitacionId (string)');
    processSnapshot(snapshot2, 'habitacionId (número)');
    processSnapshot(snapshot3, 'roomId (string)');
    processSnapshot(snapshot4, 'roomId (número)');
    
    const reservas = Array.from(reservasMap.values());
    
    console.log(`📊 Se encontraron ${reservas.length} reservas para la habitación ${habitacionId}`);
    
    for (const reserva of reservas) {
      console.log(`📝 Evaluando reserva:`, { 
        id: reserva.id,
        nombre: reserva.nombre || reserva.name,
        habitacionId: reserva.habitacionId,
        roomId: reserva.roomId,
        fechaEntrada: reserva.fechaEntrada || reserva.checkIn,
        fechaSalida: reserva.fechaSalida || reserva.checkOut,
        esBloqueo: reserva.esBloqueo,
        bloqueado: reserva.bloqueado,
        tipo: reserva.tipo,
        estado: reserva.estado || reserva.status
      });
      
      // Convertir timestamps de Firebase a objetos Date
      let reservaFechaEntrada = reserva.fechaEntrada instanceof Timestamp 
        ? reserva.fechaEntrada.toDate() 
        : reserva.checkIn instanceof Timestamp
          ? reserva.checkIn.toDate()
          : new Date(reserva.fechaEntrada || reserva.checkIn);
        
      let reservaFechaSalida = reserva.fechaSalida instanceof Timestamp 
        ? reserva.fechaSalida.toDate() 
        : reserva.checkOut instanceof Timestamp
          ? reserva.checkOut.toDate()
          : new Date(reserva.fechaSalida || reserva.checkOut);
      
      // Comprobar si hay solapamiento de fechas
      // Comparamos solo la fecha, ignorando la hora
      const fechaEntradaComp = new Date(fechaEntradaObj.getFullYear(), fechaEntradaObj.getMonth(), fechaEntradaObj.getDate());
      const fechaSalidaComp = new Date(fechaSalidaObj.getFullYear(), fechaSalidaObj.getMonth(), fechaSalidaObj.getDate());
      const reservaEntradaComp = new Date(reservaFechaEntrada.getFullYear(), reservaFechaEntrada.getMonth(), reservaFechaEntrada.getDate());
      const reservaSalidaComp = new Date(reservaFechaSalida.getFullYear(), reservaFechaSalida.getMonth(), reservaFechaSalida.getDate());
      
      console.log(`📅 Comparando fechas: ${fechaEntradaComp.toISOString().split('T')[0]} a ${fechaSalidaComp.toISOString().split('T')[0]} con ${reservaEntradaComp.toISOString().split('T')[0]} a ${reservaSalidaComp.toISOString().split('T')[0]}`);
      
      if (
        (fechaEntradaComp >= reservaEntradaComp && fechaEntradaComp < reservaSalidaComp) ||
        (fechaSalidaComp > reservaEntradaComp && fechaSalidaComp <= reservaSalidaComp) ||
        (fechaEntradaComp <= reservaEntradaComp && fechaSalidaComp >= reservaSalidaComp)
      ) {
        console.log(`❌ Conflicto de fechas detectado - Habitación ${habitacionId} no disponible`);
        return false;
      }
    }

    console.log(`✅ Habitación ${habitacionId} disponible para las fechas solicitadas`);
    return true;
  } catch (error) {
    console.error('❌ Error al verificar disponibilidad:', error);
    throw error;
  }
};

// Función para anular reservas por código
export const anularReservaPorCodigo = async (codigoReserva) => {
  try {
    console.log('Anulando reservas con código:', codigoReserva);
    
    // Buscar todas las reservas con el código de reserva
    const q = query(
      collection(db, 'reservas'),
      where('codigoReserva', '==', codigoReserva)
    );

    const querySnapshot = await getDocs(q);
    console.log('Reservas encontradas:', querySnapshot.size);
    
    if (querySnapshot.empty) {
      throw new Error('No se encontraron reservas con este código');
    }

    // Registrar las reservas que se eliminarán
    querySnapshot.forEach((doc) => {
      console.log('Eliminando reserva ID:', doc.id, 'Datos:', doc.data());
    });

    // Eliminar todas las reservas encontradas
    const promesasEliminacion = [];
    querySnapshot.forEach((doc) => {
      promesasEliminacion.push(deleteDoc(doc.ref));
    });

    await Promise.all(promesasEliminacion);
    console.log('Eliminación completada. Total eliminadas:', promesasEliminacion.length);
    
    return {
      success: true,
      mensaje: 'Reserva anulada correctamente',
      cantidad: querySnapshot.size
    };
  } catch (error) {
    console.error('Error al anular la reserva:', error);
    throw error;
  }
};

// Función para buscar reservas por código
export const buscarReservaPorCodigo = async (codigoReserva) => {
  try {
    console.log('Buscando reservas con código:', codigoReserva);
    
    // Buscar todas las reservas con el código de reserva
    const q = query(
      collection(db, 'reservas'),
      where('codigoReserva', '==', codigoReserva)
    );

    const querySnapshot = await getDocs(q);
    console.log('Reservas encontradas:', querySnapshot.size);
    
    if (querySnapshot.empty) {
      throw new Error('No se encontraron reservas con este código');
    }

    // Procesar las reservas encontradas
    const reservas = [];
    let datosComunes = {};
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Datos comunes que se comparten entre todas las reservas con este código
      if (Object.keys(datosComunes).length === 0) {
        datosComunes = {
          nombre: data.nombre,
          email: data.email,
          telefono: data.telefono,
          fechaEntrada: data.fechaEntrada instanceof Timestamp 
            ? data.fechaEntrada.toDate().toISOString().split('T')[0]
            : data.fechaEntrada,
          fechaSalida: data.fechaSalida instanceof Timestamp 
            ? data.fechaSalida.toDate().toISOString().split('T')[0]
            : data.fechaSalida,
          codigoReserva: data.codigoReserva,
          fechaCreacion: data.fechaCreacion instanceof Timestamp 
            ? data.fechaCreacion.toDate().toLocaleDateString('es-ES')
            : data.fechaCreacion
        };
      }
      
      // Datos específicos de cada habitación
      reservas.push({
        id: doc.id,
        habitacionId: data.habitacionId,
        personasEnHabitacion: data.personasEnHabitacion,
        precioHabitacion: data.precioHabitacion,
        precioTotal: data.precioTotal
      });
    });
    
    return {
      datosComunes,
      habitaciones: reservas,
      cantidadHabitaciones: reservas.length,
      precioTotal: reservas.reduce((sum, r) => sum + (r.precioTotal || 0), 0)
    };
  } catch (error) {
    console.error('Error al buscar la reserva:', error);
    throw error;
  }
};

// Función para eliminar una habitación específica de una reserva
export const eliminarHabitacionDeReserva = async (reservaId) => {
  try {
    console.log('Eliminando habitación con ID:', reservaId);
    
    // Buscar la reserva específica
    const docRef = doc(collection(db, 'reservas'), reservaId);
    await deleteDoc(docRef);
    
    return {
      success: true,
      mensaje: 'Habitación eliminada correctamente'
    };
  } catch (error) {
    console.error('Error al eliminar la habitación:', error);
    throw error;
  }
};

// Función para obtener la capacidad máxima de una habitación
// Como medida provisional hasta integrar con las habitaciones reales
const obtenerCapacidadMaxima = async (habitacionId) => {
  try {
    // 1) Intentar obtener la habitación desde Firestore (fuente de verdad)
    const habitacionIdStr = habitacionId?.toString?.() ?? String(habitacionId);
    const habitacionDocRef = doc(collection(db, 'habitaciones'), habitacionIdStr);
    const habitacionDocSnap = await getDoc(habitacionDocRef);

    if (habitacionDocSnap.exists()) {
      const data = habitacionDocSnap.data();
      const capacidad = data.capacity || data.capacidad || 0;
      if (capacidad) {
        console.log(`Capacidad (Firestore) para habitación ${habitacionIdStr}: ${capacidad}`);
        return capacidad;
      }
    }

    // Buscar la habitación en el array importado
    const habitacion = habitaciones.find(h => h.id.toString() === habitacionId.toString());
    
    // Si encontramos la habitación, devolvemos su capacidad
    if (habitacion) {
      console.log(`Capacidad para habitación ${habitacionId}: ${habitacion.capacidad}`);
      return habitacion.capacidad;
    }
    
    // Valores por defecto en caso de que no se encuentre la habitación
    console.log(`No se encontró capacidad para habitación ${habitacionId}, usando valor por defecto: 2`);
    return 2; // Valor por defecto en caso de que no se encuentre
  } catch (error) {
    console.error('Error al obtener capacidad máxima:', error);
    return 2; // Valor por defecto en caso de error
  }
};

// Obtener el precio por noche según ocupación desde Firestore (fallback a datos locales)
const obtenerPrecioPorOcupacionHabitacion = async (habitacionId, ocupacion) => {
  const ocupacionNum = parseInt(ocupacion, 10) || 1;
  const habitacionIdStr = habitacionId?.toString?.() ?? String(habitacionId);

  try {
    const habitacionDocRef = doc(collection(db, 'habitaciones'), habitacionIdStr);
    const habitacionDocSnap = await getDoc(habitacionDocRef);

    if (habitacionDocSnap.exists()) {
      const data = habitacionDocSnap.data();
      const capacidad = data.capacity || data.capacidad || 1;
      const ocupacionAjustada = Math.min(ocupacionNum, capacidad);
      const preciosPorOcupacion = data.preciosPorOcupacion || {};

      const precioOcupacion = preciosPorOcupacion[ocupacionAjustada];
      if (precioOcupacion !== undefined && precioOcupacion !== null) {
        return parseInt(precioOcupacion, 10) || 0;
      }
      return parseInt(data.precio, 10) || 0;
    }
  } catch (error) {
    console.warn('No se pudo obtener precio desde Firestore, usando fallback local.', error);
  }

  // Fallback: datos locales (pueden estar desactualizados)
  const habitacionLocal = habitaciones.find(h => h.id.toString() === habitacionIdStr);
  if (habitacionLocal) {
    const capacidad = habitacionLocal.capacity || habitacionLocal.capacidad || 1;
    const ocupacionAjustada = Math.min(ocupacionNum, capacidad);
    const preciosPorOcupacion = habitacionLocal.preciosPorOcupacion || {};
    const precioOcupacion = preciosPorOcupacion[ocupacionAjustada];
    if (precioOcupacion !== undefined && precioOcupacion !== null) {
      return parseInt(precioOcupacion, 10) || 0;
    }
    return parseInt(habitacionLocal.precio, 10) || 0;
  }

  return 0;
};

// Función para actualizar el número de personas en una habitación
export const actualizarPersonasEnHabitacion = async (reservaId, nuevaCantidadPersonas) => {
  try {
    console.log('Actualizando personas en habitación:', reservaId);
    
    // Buscar la reserva específica
    const docRef = doc(collection(db, 'reservas'), reservaId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('No se encontró la habitación con el ID proporcionado');
    }
    
    const reservaData = docSnap.data();
    
    // Verificar que no exceda la capacidad máxima de la habitación
    const capacidadMaxima = await obtenerCapacidadMaxima(reservaData.habitacionId);
    
    if (nuevaCantidadPersonas > capacidadMaxima) {
      throw new Error(`El número de personas excede la capacidad máxima de la habitación (${capacidadMaxima} personas)`);
    }
    
    // Obtener el nuevo precio base según la ocupación (preferir Firestore)
    const nuevoPrecioBase = await obtenerPrecioPorOcupacionHabitacion(
      reservaData.habitacionId,
      nuevaCantidadPersonas
    );
    
    // Calcular la duración en días para el precio total
    let duracionEstancia = reservaData.duracionEstancia;
    
    if (!duracionEstancia) {
      // Si no existe duracionEstancia, calcularla a partir de las fechas
      const fechaEntrada = reservaData.fechaEntrada instanceof Timestamp 
        ? reservaData.fechaEntrada.toDate() 
        : new Date(reservaData.fechaEntrada);
        
      const fechaSalida = reservaData.fechaSalida instanceof Timestamp 
        ? reservaData.fechaSalida.toDate() 
        : new Date(reservaData.fechaSalida);
      
      const diferencia = fechaSalida.getTime() - fechaEntrada.getTime();
      duracionEstancia = Math.round(diferencia / (1000 * 60 * 60 * 24));
    }
    
    // Calcular el nuevo precio total
    const nuevoPrecioTotal = nuevoPrecioBase * duracionEstancia;
    
    // Actualizar la reserva con los nuevos valores
    await updateDoc(docRef, {
      personasEnHabitacion: nuevaCantidadPersonas,
      precioHabitacion: nuevoPrecioBase,
      precioTotal: nuevoPrecioTotal
    });
    
    return {
      success: true,
      mensaje: 'Número de personas y precios actualizados correctamente'
    };
  } catch (error) {
    console.error('Error al actualizar personas en la habitación:', error);
    throw error;
  }
};

// Función para actualizar las fechas de una reserva completa (todas las habitaciones)
export const actualizarFechasReserva = async (codigoReserva, nuevaFechaEntrada, nuevaFechaSalida) => {
  try {
    console.log('Actualizando fechas para código de reserva:', codigoReserva);
    
    // Normalizar las fechas
    const fechaEntradaNormalizada = normalizarFormatoFecha(nuevaFechaEntrada);
    const fechaSalidaNormalizada = normalizarFormatoFecha(nuevaFechaSalida);
    
    // Parseamos manualmente la fecha
    const [anioEntrada, mesEntrada, diaEntrada] = fechaEntradaNormalizada.split('-');
    const [anioSalida, mesSalida, diaSalida] = fechaSalidaNormalizada.split('-');
    
    // Crear fechas con la zona horaria de Chile
    const fechaEntradaObj = crearFechaChile(anioEntrada, mesEntrada, diaEntrada);
    const fechaSalidaObj = crearFechaChile(anioSalida, mesSalida, diaSalida);
    
    // Crear timestamps para Firebase
    const fechaEntradaTimestamp = Timestamp.fromDate(fechaEntradaObj);
    const fechaSalidaTimestamp = Timestamp.fromDate(fechaSalidaObj);
    
    // Calcular la nueva duración en días
    const diferencia = fechaSalidaObj.getTime() - fechaEntradaObj.getTime();
    const nuevaDuracionEstancia = Math.round(diferencia / (1000 * 60 * 60 * 24));
    
    // Buscar todas las reservas con el código
    const q = query(
      collection(db, 'reservas'),
      where('codigoReserva', '==', codigoReserva)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('No se encontraron reservas con este código');
    }
    
    // Verificar disponibilidad para las nuevas fechas
    const habitacionesIds = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      habitacionesIds.push(data.habitacionId);
    });
    
    // Verificar disponibilidad para cada habitación (excepto las propias)
    const disponibilidadPromises = habitacionesIds.map(id => 
      verificarDisponibilidadPorFechas(id, fechaEntradaNormalizada, fechaSalidaNormalizada, codigoReserva)
    );
    
    const resultadosDisponibilidad = await Promise.all(disponibilidadPromises);
    
    // Si alguna habitación no está disponible, cancelar la operación
    const todasDisponibles = resultadosDisponibilidad.every(disponible => disponible);
    
    if (!todasDisponibles) {
      throw new Error('No se pueden cambiar las fechas porque alguna habitación no está disponible para el nuevo período');
    }
    
    // Actualizar todas las reservas con las nuevas fechas y precios recalculados
    const actualizacionesPromises = [];
    querySnapshot.forEach((documento) => {
      const data = documento.data();
      // Calcular el nuevo precio total basado en la nueva duración
      const nuevoPrecioTotal = data.precioHabitacion * nuevaDuracionEstancia;
      
      actualizacionesPromises.push(
        updateDoc(documento.ref, {
          fechaEntrada: fechaEntradaTimestamp,
          fechaSalida: fechaSalidaTimestamp,
          duracionEstancia: nuevaDuracionEstancia,
          precioTotal: nuevoPrecioTotal
        })
      );
    });
    
    await Promise.all(actualizacionesPromises);
    
    return {
      success: true,
      mensaje: 'Fechas y precios actualizados correctamente',
      cantidad: querySnapshot.size
    };
  } catch (error) {
    console.error('Error al actualizar las fechas:', error);
    throw error;
  }
};

// Función para verificar disponibilidad por fechas, excluyendo reservas propias
export const verificarDisponibilidadPorFechas = async (habitacionId, fechaEntrada, fechaSalida, codigoReservaExcluir = null) => {
  try {
    console.log(`Verificando disponibilidad para habitación ${habitacionId} del ${fechaEntrada} al ${fechaSalida}, excluyendo código ${codigoReservaExcluir}`);
    
    // Normalizar las fechas
    const fechaEntradaNormalizada = normalizarFormatoFecha(fechaEntrada);
    const fechaSalidaNormalizada = normalizarFormatoFecha(fechaSalida);
    
    // Parseamos manualmente la fecha
    const [anioEntrada, mesEntrada, diaEntrada] = fechaEntradaNormalizada.split('-');
    const [anioSalida, mesSalida, diaSalida] = fechaSalidaNormalizada.split('-');
    
    // Crear fechas con la zona horaria de Chile
    const fechaEntradaObj = crearFechaChile(anioEntrada, mesEntrada, diaEntrada);
    const fechaSalidaObj = crearFechaChile(anioSalida, mesSalida, diaSalida);

    // Buscar todas las reservas para esta habitación (sin filtrar por código)
    const q = query(
      collection(db, 'reservas'),
      where('habitacionId', '==', habitacionId)
    );

    const querySnapshot = await getDocs(q);
    console.log(`Se encontraron ${querySnapshot.size} reservas para la habitación ${habitacionId}`);
    
    // Iterar sobre los resultados y filtrar manualmente las que tienen el mismo código
    for (const doc of querySnapshot.docs) {
      const reserva = doc.data();
      
      // Saltar esta reserva si tiene el mismo código (es la propia reserva)
      if (codigoReservaExcluir && reserva.codigoReserva === codigoReservaExcluir) {
        console.log(`Ignorando reserva con mismo código: ${reserva.codigoReserva}`);
        continue;
      }
      
      // Convertir timestamps de Firebase a objetos Date
      const reservaFechaEntrada = reserva.fechaEntrada instanceof Timestamp 
        ? reserva.fechaEntrada.toDate() 
        : new Date(reserva.fechaEntrada);
        
      const reservaFechaSalida = reserva.fechaSalida instanceof Timestamp 
        ? reserva.fechaSalida.toDate() 
        : new Date(reserva.fechaSalida);
      
      // Comprobar si hay solapamiento de fechas
      // Comparamos solo la fecha, ignorando la hora
      const fechaEntradaComp = new Date(fechaEntradaObj.getFullYear(), fechaEntradaObj.getMonth(), fechaEntradaObj.getDate());
      const fechaSalidaComp = new Date(fechaSalidaObj.getFullYear(), fechaSalidaObj.getMonth(), fechaSalidaObj.getDate());
      const reservaEntradaComp = new Date(reservaFechaEntrada.getFullYear(), reservaFechaEntrada.getMonth(), reservaFechaEntrada.getDate());
      const reservaSalidaComp = new Date(reservaFechaSalida.getFullYear(), reservaFechaSalida.getMonth(), reservaFechaSalida.getDate());
      
      console.log(`Comparando fechas: ${fechaEntradaComp.toISOString().split('T')[0]} a ${fechaSalidaComp.toISOString().split('T')[0]} con ${reservaEntradaComp.toISOString().split('T')[0]} a ${reservaSalidaComp.toISOString().split('T')[0]}`);
      
      // Lógica de solapamiento modificada: permitimos que una reserva comience el mismo día que otra termina
      if (
        // Caso 1: La fecha de entrada solicitada está dentro del período de otra reserva
        (fechaEntradaComp > reservaEntradaComp && fechaEntradaComp < reservaSalidaComp) ||
        // Caso 2: La fecha de salida solicitada está dentro del período de otra reserva
        (fechaSalidaComp > reservaEntradaComp && fechaSalidaComp < reservaSalidaComp) ||
        // Caso 3: El período solicitado contiene completamente otra reserva
        (fechaEntradaComp <= reservaEntradaComp && fechaSalidaComp >= reservaSalidaComp) ||
        // Eliminamos el Caso 4 que impedía reservar con entrada el mismo día que otra sale
        // Caso 5: La fecha de salida solicitada es igual a la fecha de entrada de otra reserva
        (fechaSalidaComp.getTime() === reservaEntradaComp.getTime())
      ) {
        console.log(`¡Conflicto detectado! No hay disponibilidad.`);
        return false;
      }
    }

    console.log('Verificación completada: Habitación disponible para las fechas solicitadas');
    return true;
  } catch (error) {
    console.error('Error al verificar disponibilidad por fechas:', error);
    throw error;
  }
}; 